import { forwardRef, type HTMLAttributes, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';

// No `elevated` variant: resting cards never carry shadows (paper, not glass —
// hairlines define surfaces; shadows are reserved for overlays).
export type CardVariant = 'solid' | 'outlined' | 'dashed';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  interactive?: boolean;
}

const variantClasses: Record<CardVariant, string> = {
  solid: 'bg-card border border-line-2',
  outlined: 'bg-transparent border border-line-2',
  dashed: 'bg-card border border-dashed border-line-3',
};

const paddingClasses: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'solid', padding = 'md', interactive = false, className, onKeyDown, ...rest },
  ref,
) {
  // An interactive card is a clickable surface — make it keyboard-operable
  // (role/tabIndex + Enter/Space → click) and give it the standard focus ring,
  // and gate its hover behind can-hover so touch devices don't stick.
  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(e);
    if (interactive && !e.defaultPrevented && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.currentTarget.click();
    }
  }

  return (
    <div
      ref={ref}
      data-variant={variant}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? handleKeyDown : onKeyDown}
      className={cn(
        'rounded-xl transition-colors duration-press ease-out-strong',
        variantClasses[variant],
        paddingClasses[padding],
        interactive &&
          'pressable cursor-pointer can-hover:hover:border-line-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        className,
      )}
      {...rest}
    />
  );
});
